import { Button, Tag } from '@carbon/react';
import { TractNRI } from 'components/nri-table';
import { Slot } from '../types';
import { SlotInterestInfo } from '../nri/slot-interest-info';
import styles from './slot-list.module.scss';
import classNames from 'classnames';

/**
 * Formats allocation method for display (e.g., "lateral_length" -> "Lateral Length").
 * @param {string | undefined} method - Raw allocation method.
 * @return {string} Formatted string.
 */
const formatAllocationMethod = (method: string | undefined): string => {
  if (!method) return '--';
  return method
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface SlotListProps {
  slots: Slot[];
  selectedSlotId: string | undefined;
  setSelectedSlotId: (slotId: string) => void;
  onManageSlots?: () => void;
  slotNriByUwi?: Map<string, TractNRI>;
  /** Set of slot IDs (UWIs) that have at least one UAT. Slots NOT in this set render interactive verification controls. */
  slotsWithUats?: Set<string>;
}

/**
 * Slot list with optional Interest Info cards. Each slot renders as a single
 * card row: details on the left, Interest Info on the right separated by a border.
 * @param {SlotListProps} props - The component props.
 * @return {JSX.Element} The slot list.
 */
export const SlotList = ({
  slots,
  selectedSlotId,
  setSelectedSlotId,
  onManageSlots,
  slotNriByUwi,
  slotsWithUats,
}: SlotListProps) => {
  return (
    <div className={styles.container}>
      <div className={styles.stickyHeader}>
        <h4 className={styles.title}>Slots</h4>
        {onManageSlots && (
          <Button
            kind="ghost"
            size="sm"
            onClick={onManageSlots}
            data-testid="manage-slots-button"
          >
            Manage Slots
          </Button>
        )}
      </div>
      <div className={styles.scrollableContent}>
        {slots.map((slot) => {
          const nriData = slotNriByUwi?.get(slot.id);

          return (
            <div
              key={slot.id}
              onClick={() => setSelectedSlotId(slot.id)}
              className={classNames(styles.slotCard, {
                [styles.selected]: slot.id === selectedSlotId,
                [styles.excluded]: slot.isWellboreExcluded,
              })}
            >
              <div className={styles.slotLeft}>
                <div className={styles.slotHeader}>
                  <span className={styles.slotLink}>{slot.id}</span>
                  <span className={styles.slotBadge}>
                    ({slot.status ?? ''})
                  </span>
                  {slot.isWellboreExcluded && (
                    <Tag
                      type="gray"
                      size="sm"
                      title={slot.wellboreExclusionReason ?? undefined}
                    >
                      Wellbore Excluded
                    </Tag>
                  )}
                </div>
                <div className={styles.slotMeta}>
                  {/* Prefer the server-built combined well name (lease #number)
                      from slot_allocations; fall back to the lease name when a
                      slot has no matching NRI row. */}
                  <div>{nriData?.wellName ?? slot.name ?? '--'}</div>
                  {slot.operator && <div>{slot.operator}</div>}
                  <div>
                    Allocation Method:{' '}
                    {formatAllocationMethod(slot.allocationMethod)}
                  </div>
                </div>
              </div>
              {nriData && (
                <div className={styles.slotRight}>
                  <SlotInterestInfo
                    slotNri={nriData.totalSlotNri ?? null}
                    slotWi={nriData.totalSlotWi ?? null}
                    subtracts={nriData.subtracts ?? []}
                    tractId={nriData.crmTractSalesforceId ?? ''}
                    slotId={nriData.slotId ?? slot.id}
                    wellUwi={nriData.wellId || null}
                    wellName={nriData.wellName || slot.name || null}
                    hasUats={slotsWithUats?.has(slot.id) ?? true}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
